import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../layout/Layout';

interface RelationshipStatus {
  id: number;
  name: string;
  created_at: string;
}

const RelationshipStatuses: React.FC = () => {
  const [relationshipStatuses, setRelationshipStatuses] = useState<RelationshipStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    id: null as number | null,
    name: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchRelationshipStatuses();
  }, []);

  const fetchRelationshipStatuses = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/admin/app-assets/relationship-statuses');
      if (response.data.success) {
        setRelationshipStatuses(response.data.data.relationship_statuses);
      }
    } catch (error) {
      console.error('Error fetching relationship statuses:', error);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (status?: RelationshipStatus) => {
    if (status) {
      setFormData({ id: status.id, name: status.name });
    } else {
      setFormData({ id: null, name: '' });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setFormData({ id: null, name: '' });
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      alert('Please enter a name');
      return;
    }

    try {
      setSubmitting(true);
      if (formData.id) {
        await axios.put(`/admin/app-assets/relationship-statuses/${formData.id}`, {
          name: formData.name
        });
        alert('Relationship status updated successfully!');
      } else {
        await axios.post('/admin/app-assets/relationship-statuses', {
          name: formData.name
        });
        alert('Relationship status created successfully!');
      }
      closeModal();
      await fetchRelationshipStatuses();
    } catch (error: any) {
      console.error('Error saving relationship status:', error);
      alert(error.response?.data?.message || 'Failed to save relationship status');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number, name: string) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) {
      return;
    }

    try {
      await axios.delete(`/admin/app-assets/relationship-statuses/${id}`);
      alert('Relationship status deleted successfully!');
      await fetchRelationshipStatuses();
    } catch (error: any) {
      console.error('Error deleting relationship status:', error);
      alert(error.response?.data?.message || 'Failed to delete relationship status');
    }
  };

  return (
    <Layout>
      <div>
        <div className="row">
          <div className="col-12">
            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
              <div>
                <h4 className="mb-1 font-size-24 fw-semibold" style={{ color: '#495057' }}>Relationship Statuses</h4>
                <p className="text-muted mb-0">Manage relationship status options</p>
              </div>
              <div className="page-title-right">
                <button className="btn btn-primary" onClick={() => openModal()}>
                  <i className="bx bx-plus me-1"></i>
                  Add New
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : relationshipStatuses.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bx bx-heart font-size-48 text-muted mb-3 d-block"></i>
                    <p className="text-muted">No relationship statuses found</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>ID</th>
                          <th>Name</th>
                          <th>Created Date</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {relationshipStatuses.map(status => (
                          <tr key={status.id}>
                            <td>{status.id}</td>
                            <td className="fw-semibold">{status.name}</td>
                            <td>
                              <small>{new Date(status.created_at).toLocaleDateString()}</small>
                            </td>
                            <td>
                              <button
                                className="btn btn-sm btn-warning me-2"
                                onClick={() => openModal(status)}
                              >
                                <i className="bx bx-edit"></i>
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => handleDelete(status.id, status.name)}
                              >
                                <i className="bx bx-trash"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Add/Edit Modal */}
        {showModal && (
          <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {formData.id ? 'Edit Relationship Status' : 'Add New Relationship Status'}
                  </h5>
                  <button type="button" className="btn-close" onClick={closeModal}></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Name *</label>
                    <input 
                      type="text" 
                      className="form-control"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Enter relationship status"
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeModal}>
                    Cancel
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-primary"
                    onClick={handleSubmit}
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="bx bx-save me-1"></i>
                        Save
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default RelationshipStatuses;





